/** Fenêtre géographique visible sur la carte. */
export interface MapArea {
  readonly south: number;
  readonly west: number;
  readonly north: number;
  readonly east: number;
}

export interface GeoPoint {
  readonly latitude: number;
  readonly longitude: number;
}

/**
 * Ce que la carte doit montrer, à la demande de l'application.
 *
 * Deux formes, parce que les besoins diffèrent : une ville se cadre sur son
 * emprise, une position GPS se centre avec un niveau de zoom choisi. Chaque
 * nouvel objet déclenche un mouvement, y compris pour le même lieu.
 */
export type MapFocus =
  | {
      readonly kind: "point";
      readonly latitude: number;
      readonly longitude: number;
      readonly zoom?: number;
    }
  | {
      readonly kind: "area";
      readonly south: number;
      readonly west: number;
      readonly north: number;
      readonly east: number;
    };

/**
 * Au-delà de cette étendue, une emprise ne renseigne plus rien d'utile.
 *
 * Nominatim donne à « France » une emprise qui englobe la Guyane, La Réunion et
 * la Nouvelle-Calédonie : cadrer dessus revient à afficher la planète. Passé ce
 * seuil, on préfère centrer sur le point avec un zoom régional.
 */
const MAX_USEFUL_SPAN_IN_DEGREES = 24;

const spans = (envelope: { south: number; west: number; north: number; east: number }): boolean =>
  envelope.north - envelope.south > MAX_USEFUL_SPAN_IN_DEGREES ||
  envelope.east - envelope.west > MAX_USEFUL_SPAN_IN_DEGREES;

/** Zoom de repli quand l'emprise d'un lieu est trop vaste pour être cadrée. */
const FALLBACK_ZOOM = 9;

/** Traduit un lieu trouvé en consigne de cadrage pour la carte. */
export const focusOnPlace = (place: {
  latitude: number;
  longitude: number;
  envelope: { south: number; west: number; north: number; east: number } | null;
}): MapFocus =>
  place.envelope !== null && !spans(place.envelope)
    ? { kind: "area", ...place.envelope }
    : {
        kind: "point",
        latitude: place.latitude,
        longitude: place.longitude,
        zoom: place.envelope === null ? 16 : FALLBACK_ZOOM,
      };
